import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from "react";

type TableContextType = {
  filters: any;
  currentPage: number;
  changePage: (page: number) => void;
  changeFilters: (field: string, values: string[] | string | number[]) => void;
  filtersChanges: number;
  resetFilters: () => void;
  replaceFilter: (filter: any) => void;
  onSearchQChange: (value: string) => void;
  searchQ: string;
};
const TableFiltersContext = createContext<TableContextType | null>(null);

export default function TableFiltersContextProvider({
  children,
}: {
  children: ReactNode;
}) {
  const [searchQ, setSearchQ] = useState<string>("");
  const [filters, setFilters] = useState<any>({});
  const [currentPage, setCurrentPage] = useState<number>(0);
  const [filtersChanges, setFiltersChanges] = useState<number>(0);

  const changePage = (page: number) => {
    setCurrentPage(page - 1);
  };

  const onSearchQChange = (value: string) => {
    setSearchQ(value);
    setFiltersChanges(1);
    setCurrentPage(0);
  };

  const changeFilters = (
    field: string,
    values: string | string[] | number[]
  ) => {
    setFilters((prevFilters: any) => {
      const [parentKey, childKey] = field.split("-") as string[];
      if (!childKey) {
        return {
          ...prevFilters,
          [parentKey]: values,
        };
      }

      return {
        ...prevFilters,
        [parentKey]: {
          ...(prevFilters[parentKey] &&
          typeof prevFilters[parentKey] === "object" &&
          !Array.isArray(prevFilters[parentKey])
            ? prevFilters[parentKey]
            : {}),
          [childKey]: values,
        },
      };
    });
    setFiltersChanges((state) => state + 1);
  };

  const resetFilters = () => {
    setFilters({});
    setCurrentPage(0);
    setFiltersChanges(0);
    setSearchQ("");
  };

  const replaceFilter = (filter: any) => {
    setFilters(filter);
    setFiltersChanges(1);
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [filters]);

  return (
    <TableFiltersContext.Provider
      value={{
        filters,
        currentPage,
        changePage,
        changeFilters,
        filtersChanges,
        resetFilters,
        replaceFilter,
        onSearchQChange,
        searchQ,
      }}
    >
      {children}
    </TableFiltersContext.Provider>
  );
}

export const useTableContext = (): TableContextType => {
  const context = useContext(TableFiltersContext);
  if (!context) {
    throw new Error("useContext must be used within a TableProvider");
  }
  return context;
};
